import React from 'react';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  TrendingUp,
  Calendar,
  Target
} from 'lucide-react';

const TaskStats = ({ stats, completedTasks = [] }) => {
  if (!stats) return null;

  const {
    not_started_count = 0,
    in_progress_count = 0,
    completed_count = 0,
    completed_today = 0,
    high_priority_count = 0,
    medium_priority_count = 0,
    low_priority_count = 0,
    smart_task_count = 0,
    manual_task_count = 0,
    total_tasks = 0
  } = stats;

  // Convert string values to numbers (backend returns strings)
  const notStartedCount = parseInt(not_started_count) || 0;
  const inProgressCount = parseInt(in_progress_count) || 0;
  const completedCount = parseInt(completed_count) || 0;
  const completedToday = parseInt(completed_today) || 0;
  const highPriorityCount = parseInt(high_priority_count) || 0;
  const mediumPriorityCount = parseInt(medium_priority_count) || 0;
  const lowPriorityCount = parseInt(low_priority_count) || 0;
  const smartTaskCount = parseInt(smart_task_count) || 0;
  const manualTaskCount = parseInt(manual_task_count) || 0;
  const totalTasksCount = parseInt(total_tasks) || 0;

  const totalTasks = notStartedCount + inProgressCount + completedCount;
  const activeTasks = notStartedCount + inProgressCount;
  
  // For Task Status section: show percentages of active tasks only
  const completionRate = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const inProgressRate = activeTasks > 0 ? Math.round((inProgressCount / activeTasks) * 100) : 0;
  const notStartedRate = activeTasks > 0 ? Math.round((notStartedCount / activeTasks) * 100) : 0;
  
  // Debug logging
  console.log('TaskStats Debug:', {
    not_started_count,
    in_progress_count,
    completed_count,
    notStartedCount,
    inProgressCount,
    completedCount,
    totalTasks,
    activeTasks,
    notStartedRate,
    inProgressRate,
    completionRate
  });

  return (
    <div className="space-y-6">
      {/* Task Status Overview */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-5 w-5 text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-900">Task Status</h3>
        </div>
        
        <div className="space-y-4">
          {/* Completed */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-xs font-medium text-gray-700">Completed</span>
            </div>
            <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-900">{completionRate}%</span>
                  <span className="text-xs text-gray-500">({completedCount})</span>
            </div>
          </div>

          {/* In Progress */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span className="text-xs font-medium text-gray-700">In Progress</span>
            </div>
            <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-900">{inProgressRate}%</span>
                  <span className="text-xs text-gray-500">({inProgressCount})</span>
            </div>
          </div>

          {/* Not Started */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-xs font-medium text-gray-700">Not Started</span>
            </div>
            <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-900">{notStartedRate}%</span>
                  <span className="text-xs text-gray-500">({notStartedCount})</span>
            </div>
          </div>
        </div>
      </div>

          {/* Quick Stats */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-900">Quick Stats</h3>
        </div>
        
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-green-50 to-green-100">
                <div className="text-lg font-bold text-green-600">{completedToday}</div>
                <div className="text-xs text-gray-600 font-medium">Completed Today</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-red-50 to-red-100">
                <div className="text-lg font-bold text-red-600">{highPriorityCount}</div>
                <div className="text-xs text-gray-600 font-medium">High Priority</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100">
                <div className="text-lg font-bold text-purple-600">{smartTaskCount}</div>
                <div className="text-xs text-gray-600 font-medium">Smart Tasks</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100">
                <div className="text-lg font-bold text-blue-600">{manualTaskCount}</div>
                <div className="text-xs text-gray-600 font-medium">Manual Tasks</div>
              </div>
            </div>
      </div>

      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Completed Tasks</h3>
          </div>
          
          <div className="space-y-3">
            {completedTasks.slice(0, 5).map((task) => (
              <div key={task.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {task.title}
                  </div>
                  <div className="text-xs text-gray-500">
                    Completed {new Date(task.completed_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
            
            {completedTasks.length > 5 && (
              <div className="text-center">
                <span className="text-xs text-gray-500">
                  +{completedTasks.length - 5} more completed tasks
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State for Completed Tasks */}
      {completedTasks.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-5 w-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900">Completed Tasks</h3>
          </div>
          
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No completed tasks yet</p>
            <p className="text-xs text-gray-400 mt-1">Complete your first task to see it here!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskStats;
